import { useCallback, useEffect, useRef, type ImgHTMLAttributes } from "react";
import styled from "styled-components";

import { toRasterSize, type RasterReporter } from "./annotationRasterStage";

const RenderResourceCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

function imageDimension(image: CanvasImageSource, candidates: readonly string[]): number | null {
  const record = image as unknown as Record<string, unknown>;
  for (const candidate of candidates) {
    const value = record[candidate];
    if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

/**
 * 이미지 raster. 크기는 decode 뒤 `load`에서 읽는다(캐시로 이미 decode된 경우 포함). `src`를
 * 바꾸면 브라우저는 새 이미지가 준비될 때까지 이전 이미지를 계속 그리므로 그 크기를 유지하고,
 * 불러오지 못하면 그릴 이미지가 없으므로 크기를 지운다.
 */
export function MeasuredImage({
  onRasterChange,
  ...imageProps
}: ImgHTMLAttributes<HTMLImageElement> & {
  readonly onRasterChange?: RasterReporter;
}) {
  const reportImageSize = useCallback(
    (image: HTMLImageElement | null) => {
      if (!image) return;
      const size = image.complete ? toRasterSize(image.naturalWidth, image.naturalHeight) : null;
      onRasterChange?.(size ? { size, frameReady: true } : null);
    },
    [onRasterChange],
  );
  useEffect(() => () => onRasterChange?.(null), [onRasterChange]);

  return (
    <img
      {...imageProps}
      ref={reportImageSize}
      alt=""
      onLoad={(event) => reportImageSize(event.currentTarget)}
      onError={() => onRasterChange?.(null)}
    />
  );
}

export function AnnotationRenderResourceImage({
  image,
  kind,
  onRasterChange,
}: {
  readonly image: CanvasImageSource | string;
  readonly kind: "captured-frame" | "tracking-preview" | "media-result";
  /** 그린 이미지의 크기. 디코딩 전과 unmount 때는 `null`이다. */
  readonly onRasterChange?: RasterReporter;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const markerProps =
    kind === "captured-frame"
      ? { "data-captured-frame-raster": "" }
      : kind === "tracking-preview"
        ? { "data-tracking-preview-frame": "" }
        : { "data-media-result-frame": "" };

  useEffect(() => {
    if (typeof image === "string") return;
    const width = imageDimension(image, ["naturalWidth", "videoWidth", "displayWidth", "width"]);
    const height = imageDimension(image, [
      "naturalHeight",
      "videoHeight",
      "displayHeight",
      "height",
    ]);
    const size = width === null || height === null ? null : toRasterSize(width, height);
    onRasterChange?.(size ? { size, frameReady: true } : null);
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    canvas.width = Math.max(1, Math.round(width ?? 1));
    canvas.height = Math.max(1, Math.round(height ?? 1));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  }, [image, onRasterChange]);

  useEffect(() => () => onRasterChange?.(null), [onRasterChange]);

  return typeof image === "string" ? (
    <MeasuredImage src={image} onRasterChange={onRasterChange} {...markerProps} />
  ) : (
    <RenderResourceCanvas ref={canvasRef} {...markerProps} aria-hidden="true" />
  );
}
